import { ReactElement } from 'react';
import { Header } from '../../components/Header/Header';
import { TextField, InputAdornment, IconButton, useTheme } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Clear } from '@mui/icons-material';

interface SearchBarProps {
  handleSetSearch: (value: string) => void;
  searchInput: string;
}

/**
 * Gets the contrast color for a given hex color.
 * Converts the hex color to RGB and then calculates the YIQ value (luminance).
 * If the YIQ value is greater than 128, the color is light, otherwise it is dark.
 * @param hexColor {string}
 * @returns {string}
 */
export const getContrastYIQ = (hexColor: string): string => {
  const r = parseInt(hexColor.substr(1, 2), 16);
  const g = parseInt(hexColor.substr(3, 2), 16);
  const b = parseInt(hexColor.substr(5, 2), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  return yiq >= 128 ? 'light' : 'dark';
};

export const getSearchbarBackgroundColor = (contrast: string): string => {
  return contrast === 'dark' ? 'rgba(255, 255, 255, 0.18)' : 'rgba(0, 0, 0, 0.18)';
};

const SearchBar = ({ handleSetSearch, searchInput }: SearchBarProps) => {
  const { palette } = useTheme();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const headerBackgroundColor = palette.primary[500];

  const contrast = getContrastYIQ(headerBackgroundColor);
  const tintedOutput = getSearchbarBackgroundColor(contrast);

  return (
    <form
      data-testid="projects-dashboard-search"
      style={{
        width: '50%',
      }}
      onSubmit={(e) => e.preventDefault()}
    >
      <TextField
        placeholder="Search projects by title, engineer, client, status and reference"
        onChange={(e) => handleSetSearch(e.target.value)}
        value={searchInput}
        sx={{
          backgroundColor: tintedOutput,
          borderRadius: '28px',
          width: '100%',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              border: 'none',
            },
          },
          '& input': {
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '16px',
            lineHeight: '24px',
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon style={{ color: 'rgba(255, 255, 255, 0.6)' }} />
            </InputAdornment>
          ),
          endAdornment: searchInput && (
            <IconButton onClick={() => handleSetSearch('')} data-testid="clear-search-input">
              <Clear style={{ color: 'rgba(255, 255, 255, 0.6)' }} />
            </IconButton>
          ),
        }}
      />
    </form>
  );
};

interface ProjectPageHeaderProps {
  handleSetSearch: (value: string) => void;
  searchInput: string;
}

export function ProjectPageHeader({ handleSetSearch, searchInput }: ProjectPageHeaderProps): ReactElement {
  return (
    <Header data-testid="projectPageHeader">
      <SearchBar handleSetSearch={handleSetSearch} searchInput={searchInput} />
    </Header>
  );
}
